// Returns → Defects (Fernando 22.10: "there should be a check defects tab for a manager or a higher tier person to check
// the defects"). One row per piece in the defects box: marked faulty at polishing, or held off sale by its reason (the
// old piece of a Defect exchange, a piece the customer was told to keep). A manager puts it back on sale (stock written —
// then shelve it), writes it off (say why), and may give the return fee back (Fernando 28.10: the refund stays as
// usual; a paid refund means a second payment, said before it is done). Backend: returns.py "Defects". -- Claude, 2026-10-28
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { OrderNo } from '../OrderNo';
import { fmtWh } from '../time';
import { titleEn } from '../lineName';

const R = `${API}/returns`;

const SOURCE: Record<string, string> = {
  polishing: 'found at polishing',
  exchange_hold: 'old piece of an exchange',
  kept_hold: 'customer was told to keep it',
  declared: 'the customer registered it as faulty',
};

export function DefectsTab() {
  const [state, setState] = useState<'open' | 'decided'>('open');
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<Record<number, string>>({});
  const [orderOf, setOrderOf] = useState<Record<number, string>>({});
  const [loadErr, setLoadErr] = useState('');
  const manager = ['manager', 'admin'].includes(localStorage.getItem('wms_role') || '');

  const load = useCallback(async () => {
    const r = await authFetch(`${R}/defects?state=${state}`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setLoadErr(typeof d.detail === 'string' ? d.detail : 'Could not load the defects'); setRows([]); return; }
    setLoadErr('');
    setRows(d.defects || []);
  }, [state]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const say = (id: number, text: string) => setMsg(m => ({ ...m, [id]: text }));
  const post = async (id: number, path: string, body: any) => {
    setBusy(id);
    try {
      const r = await authFetch(`${R}/defects/${id}/${path}`, { method: 'POST', body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { say(id, `⚠ ${typeof d.detail === 'string' ? d.detail : 'Could not save'}`); return null; }
      return d;
    } finally { setBusy(null); }
  };

  const decide = async (row: any, decision: 'on_sale' | 'write_off') => {
    const piece = row.article_number || titleEn(row);
    let note: string | null = null;
    if (decision === 'write_off') {
      note = window.prompt(`Write off 1× ${piece}?\n\nWhy? (required)`);
      if (note === null) return;
      if (!note.trim()) { say(row.id, '⚠ Say why it is written off.'); return; }
    } else if (!window.confirm(`Put 1× ${piece} back on sale?\n\nThe stock is written back now — then put the piece on its shelf${row.stockplace ? ` (${row.stockplace})` : ''}.`)) return;
    const d = await post(row.id, 'decide', { decision, note: note?.trim() || null });
    if (d) { say(row.id, d.already ? `Already decided: ${d.already}` : decision === 'on_sale' ? '✓ Back on sale — put it on the shelf' : '✓ Written off'); load(); }
  };

  const waiveFee = async (row: any) => {
    const orders: any[] = row.orders || [];
    const oid = row.order_id || orderOf[row.id] || (orders.length === 1 ? orders[0].order_id : '');
    const o = orders.find(x => x.order_id === oid);
    if (!o) { say(row.id, '⚠ Choose the order this piece came from first.'); return; }
    const ref = o.refund;
    if (!ref) { say(row.id, '⚠ This piece raised no refund — there is no fee to give back.'); return; }
    if (!(ref.fee > 0)) { say(row.id, '⚠ No return fee was deducted on this refund.'); return; }
    const second = ref.processed;
    if (!window.confirm(`Give the return fee (${Number(ref.fee).toFixed(2)}) back to the customer of order ${o.order_number}?\n\n`
      + (second ? 'The refund was ALREADY PAID — this is a SECOND payment to the customer.' : 'It is added to the refund that is not paid yet.'))) return;
    const d = await post(row.id, 'waive-fee', { order_id: o.order_id });
    if (d) { say(row.id, d.already ? 'Already given back' : `✓ Fee ${Number(d.fee).toFixed(2)} given back${d.second_payment ? ' — a second payment' : ''}`); load(); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 max-w-3xl">
          {state === 'open'
            ? 'Pieces in the defects box. Put a piece back on sale (stock is written back — then shelve it) or write it off. When the piece was faulty you can give the customer the return fee back; the refund otherwise stays as it is.'
            : 'Pieces decided in the last 60 days.'}
        </p>
        <button onClick={() => setState(state === 'open' ? 'decided' : 'open')} className="text-sm text-gray-400 hover:text-white">
          {state === 'open' ? 'Show decided →' : '← Waiting'}
        </button>
      </div>
      {loadErr ? <div className="text-amber-400 text-sm">⚠ {loadErr}</div>
        : rows === null ? <div className="text-gray-600 text-sm">Loading…</div> : rows.length === 0 ? (
        <div className="text-gray-600 text-sm py-8 text-center border border-dashed border-gray-800 rounded-lg">
          {state === 'open' ? 'The defects box is empty.' : 'Nothing decided in the last 60 days.'}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(row => {
            const orders: any[] = row.orders || [];
            const chosen = row.order_id || orderOf[row.id] || (orders.length === 1 ? orders[0].order_id : '');
            const o = orders.find(x => x.order_id === chosen);
            return (
              <div key={row.id} className="bg-[#1a1d27] border border-gray-800 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-semibold text-white">{row.article_number || titleEn(row)}</span>
                  <span className="text-gray-400">{titleEn(row)}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-red-900/60 text-red-200">{SOURCE[row.source] || row.source}</span>
                  <span className="text-gray-500 text-xs">{row.return_order}</span>
                  <span className="text-gray-500 text-xs">{row.store_name}</span>
                  <span className="text-gray-500 text-xs ml-auto">{row.found_by} · {row.found_at ? fmtWh(row.found_at, 'short') : ''}</span>
                </div>
                <div className="mt-1 text-xs text-gray-400 flex items-center gap-2 flex-wrap">
                  {row.reason && <span>Reason: {row.reason}</span>}
                  <span>Order:</span>
                  {orders.length <= 1 || row.order_id ? (
                    o ? <OrderNo value={o.order_number} /> : <span>—</span>
                  ) : (
                    <select value={chosen} onChange={e => setOrderOf(m => ({ ...m, [row.id]: e.target.value }))}
                      className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5 text-gray-200">
                      <option value="">— which order? —</option>
                      {orders.map(x => <option key={x.order_id} value={x.order_id}>{x.order_number}</option>)}
                    </select>
                  )}
                  {o?.refund && <span className="text-gray-500">· refund {o.refund.processed ? 'paid' : 'not paid yet'}{o.refund.fee > 0 ? ` · return fee ${Number(o.refund.fee).toFixed(2)}` : ''}</span>}
                  {row.fee_waived && <span className="text-green-400">· fee {Number(row.fee_waived_amount).toFixed(2)} given back ({row.fee_waived_by})</span>}
                </div>
                {state === 'decided' && (
                  <div className="mt-1 text-xs text-gray-400">
                    {row.decision === 'on_sale' ? 'Put back on sale' : 'Written off'} by {row.decided_by} · {row.decided_at ? fmtWh(row.decided_at, 'short') : ''}{row.decision_note ? ` — ${row.decision_note}` : ''}
                  </div>
                )}
                {manager && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {state === 'open' && <>
                      <button disabled={busy === row.id} onClick={() => decide(row, 'on_sale')}
                        className="px-2.5 py-1 rounded bg-green-800 hover:bg-green-700 text-green-100 disabled:opacity-40">Put back on sale</button>
                      <button disabled={busy === row.id} onClick={() => decide(row, 'write_off')}
                        className="px-2.5 py-1 rounded bg-gray-800 hover:bg-red-900/60 text-gray-200 disabled:opacity-40">Write off</button>
                    </>}
                    {!row.fee_waived && o?.refund && o.refund.fee > 0 && !o.refund.fee_waived && (
                      <button disabled={busy === row.id} onClick={() => waiveFee(row)}
                        className="px-2.5 py-1 rounded border border-amber-700 text-amber-200 hover:bg-amber-900/30 disabled:opacity-40">
                        Give the return fee back
                      </button>
                    )}
                  </div>
                )}
                {!manager && state === 'open' && <div className="mt-2 text-xs text-gray-500">A manager decides — the piece stays in the defects box.</div>}
                {msg[row.id] && <div className="mt-1 text-xs text-gray-300">{msg[row.id]}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
